import React from "react";
import { Wallet, CircleDollarSign, BarChart3 } from "lucide-react";

interface BudgetPanelProps {
  loadingBudget: boolean;
  totalBudget: number;
  amountSpent: number;
  remainingBudget: number;
  allocationPercent: number;
  spentPercent: string;
  remainingPercent: string;
  userCountry?: string | null;
  formatCurrency: (value: number) => string;
}

export const BudgetPanel: React.FC<BudgetPanelProps> = ({
  loadingBudget,
  totalBudget,
  amountSpent,
  remainingBudget,
  allocationPercent,
  spentPercent,
  remainingPercent,
  userCountry,
  formatCurrency,
}) => {
  const currencyCode =
    userCountry &&
    (userCountry.toLowerCase() === "india" || userCountry.toLowerCase() === "in")
      ? "INR"
      : "USD";

  return (
    <>
      {/* Panel 2: Total Budget */}
      <div className="p-6 border-t sm:border-t-0 sm:border-l lg:border-t-0 lg:border-l border-slate-200 flex flex-col justify-between">
        <div>
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
            <Wallet className="w-5 h-5" />
          </div>
          <div className="text-[#6B7280] font-bold text-[12px] tracking-wider uppercase mt-5">
            Total Budget
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-3xl font-bold text-slate-900 tracking-tight">
              {loadingBudget ? "Loading..." : formatCurrency(totalBudget)}
            </span>
            {!loadingBudget && (
              <span className="text-[#6B7280] text-[12px] font-bold tracking-wider mt-1">
                {currencyCode}
              </span>
            )}
          </div>
        </div>
        <div className="mt-3">
          <span className="inline-flex px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-[12px] font-semibold">
            {allocationPercent}% Allocated
          </span>
        </div>
      </div>

      {/* Panel 3: Amount Spent */}
      <div className="p-6 border-t border-slate-200 lg:border-t-0 lg:border-l flex flex-col justify-between">
        <div>
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
            <CircleDollarSign className="w-5 h-5" />
          </div>
          <div className="text-[#6B7280] font-bold text-[12px] tracking-wider uppercase mt-5">
            Amount Spent
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-3xl font-bold text-slate-900 tracking-tight">
              {loadingBudget ? "Loading..." : formatCurrency(amountSpent)}
            </span>
            {!loadingBudget && (
              <span className="text-[#6B7280] text-[12px] font-bold tracking-wider mt-1">
                {currencyCode}
              </span>
            )}
          </div>
        </div>
        <div className="mt-3">
          <span className="inline-flex px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[12px] font-semibold">
            {spentPercent}% of budget
          </span>
        </div>
      </div>

      {/* Panel 4: Remaining Budget */}
      <div className="p-6 border-t sm:border-l lg:border-t-0 lg:border-l border-slate-200 flex flex-col justify-between">
        <div>
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="text-[#6B7280] font-bold text-[12px] tracking-wider uppercase mt-5">
            Remaining Budget
          </div>
          <div className="flex flex-col mt-1">
            <span className="text-3xl font-bold text-slate-900 tracking-tight">
              {loadingBudget ? "Loading..." : formatCurrency(remainingBudget)}
            </span>
            {!loadingBudget && (
              <span className="text-[#6B7280] text-[12px] font-bold tracking-wider mt-1">
                {currencyCode}
              </span>
            )}
          </div>
        </div>
        <div className="mt-3">
          <span className="inline-flex px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[12px] font-semibold">
            {remainingPercent}% remaining
          </span>
        </div>
      </div>
    </>
  );
};
